import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  CardContent,
  Box,
  IconButton,
  Typography,
} from '@material-ui/core';
import { API } from 'aws-amplify';
import Spin from 'components/common/Spin';
import CloseIcon from '@material-ui/icons/Close';
import DatePickerLabel from 'components/common/DatePickerLabel';
import Dropdown from 'components/form/Dropdown';
import { useMutation } from 'react-apollo';
import { UPDATE_SCHOOL } from 'graphql/student_college';
import ADMISSION_TYPES from 'constant/admission-types';
import STATUSES from 'constant/college-application-status';
import { ProgressSlider } from './Application';
import { useSelector } from 'react-redux';
import { platforms } from '../constants';
import CollegeInfo from './CollegeInfo';

const useStyles = makeStyles((theme) => ({
  card: {
    'width': 'auto',
    'minWidth': 600,
    'height': '100vh',
    'padding': 16,
    '& table': {
      marginBottom: 16,
    },
    '& .MuiChip-root': {
      borderRadius: 4,
      backgroundColor: '#EFF2F5',
      height: 24,
    },
    '& a': {
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      minWidth: '100%'
    }
  },
  label: {
    textTransform: 'uppercase',
    fontSize: 10,
    marginBottom: theme.spacing(1),
  },
  closeIcon: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
}));

const College = ({
  collegeId,
  school,
  location,
  onClose,
  confirm,
}) => {
  const classes = useStyles();
  const [college, setCollege] = useState();
  const [loading, setLoading] = useState(true);
  const [updateSchool] = useMutation(UPDATE_SCHOOL);
  const user = useSelector((s) => s.user.user);

  const canEdit = user.type !== 'parent';

  useEffect(() => {
    API.get('api', `/college/${collegeId}`).then((data) => {
      setCollege(data);
      setLoading(false);
    });
  }, [collegeId]);

  const onDateChange = (date) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          app_deadline: date.format('YYYY-MM-DD'),
        },
      },
    });
  };

  const onStatusChange = (val) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          status: val,
        },
      },
    });
  };

  const onProgressChange = (e, value) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          progress: value,
        },
      },
    });
  };

  const onAdmissionTypeChange = (e) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          admission_type: e.target.value,
        },
      },
    });
  };

  return (
    <div className={classes.card}>
      {loading ? (
        <CardContent>
          <Spin spinning linear />
        </CardContent>
      ) : (
        college &&
        school && (
          <>
            <Box marginBottom={3}>
              <IconButton
                className={classes.closeIcon}
                color="inherit"
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
              <Typography style={{ paddingRight: 24 }} variant="subtitle1">{college['school.name']}</Typography>
              <Typography
                color="textSecondary"
              >
                {platforms.find((s) => s.value === school?.app_platform)
                  ?.label || 'N/A'}
              </Typography>
            </Box>
            <Box
              display="flex"
              marginBottom={4}
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Box>
                <Typography component="div" variant="caption" className={classes.label}>
                  Progress
                </Typography>
                <ProgressSlider
                  disabled={!canEdit}
                  aria-label="progress slider"
                  defaultValue={school.progress}
                  onChangeCommitted={(e, value) => onProgressChange(e, value)}
                />
              </Box>
              <Box>
                <Typography component="div" variant="caption" className={classes.label}>
                  App Deadline
                </Typography>
                <DatePickerLabel
                  value={school.app_deadline}
                  onChange={onDateChange}
                />
              </Box>
              <Box flex={{ xs: 2, sm: 0 }}>
                <Typography component="div" variant="caption" className={classes.label}>
                  Admission Type
                </Typography>
                <Dropdown
                  options={ADMISSION_TYPES}
                  disabled={!canEdit}
                  id="admission_type"
                  value={school.admission_type}
                  onChange={(e) => onAdmissionTypeChange(e)}
                />
              </Box>
              <Box>
                <Typography component="div" variant="caption" className={classes.label}>
                  Decisions
                </Typography>
                <Dropdown
                  options={STATUSES}
                  id="status"
                  disabled={!canEdit}
                  value={school.status}
                  style={{ marginRight: 8 }}
                  onChange={(val) => onStatusChange(val)}
                  color={
                    STATUSES.find((s) => s.value === school.status)?.color ||
                    'inherit'
                  }
                  backgroundColor={
                    STATUSES.find((s) => s.value === school.status)
                      ?.backgroundColor || 'inherit'
                  }
                />
              </Box>
            </Box>
            <Box>
              <CollegeInfo
                school={school}
                classes={classes}
                canEdit={canEdit}
                updateSchool={updateSchool}
              />
            </Box>
          </>
        )
      )}
    </div>
  );
};

export default College;
